<template>
	<div class="wrapper">
		<div v-if="loading" class="text-center">
			<b-spinner label="Loading..." style="width: 3rem; height: 3rem;" variant="info"></b-spinner>
		</div>
		<div v-else class="animated fadeIn">
			<b-row>
				<b-col cols="12">
					<b-card header-tag="header">
						<template v-slot:header>
							<i class="icon-info"></i> Delivery Details of Purchase Order #{{purchaseOrderId}}
						</template>
						<b-card-text>
							Project Name : {{purchaseOrder.projectName}}
						</b-card-text>
						<b-card-text>
							Challan No.: {{purchaseOrder.deliveryReceiptNo}}
						</b-card-text>
						<b-card-text>
							Date of Purchase : {{purchaseOrder.purchaseDate}}
						</b-card-text>
						<b-card-text>
							Number of Devices : {{purchaseOrder.deviceQuantity}}
						</b-card-text>
						<b-card-text>
							Date of Delivery : {{purchaseOrder.deliveryDate}}
						</b-card-text>
						<b-card-text>
							Last Date of Warranty : {{purchaseOrder.warrantyLastDate}}
						</b-card-text>
						<b-card-text>
							Unit Price : {{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'BDT' }).format(purchaseOrder.unitPrice.toFixed(2))}}
						</b-card-text>
						<b-card-text>
							Software Package Fee : {{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'BDT' }).format(purchaseOrder.softwarePackageFee.toFixed(2))}}
						</b-card-text>
						<b-card-text>
							Software Version : {{purchaseOrder.versionName}}
						</b-card-text>
						<b-card-text>
							Total Amount : {{new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'BDT' }).format(this.totalAmount.toFixed(2))}}
						</b-card-text>
						<b-card-text>
							<b-button size="sm" variant="info" class="mr-1" @click="downloadDeliveryReceipt()">
								<i class="fa fa-download"></i> Download Delivery Receipt
							</b-button>
						</b-card-text>
					</b-card>
				</b-col>
			</b-row>
			<b-row>
				<b-col cols="12">
					<div>
						<genericTable :table-data="tableData" :filter="false" :fields="fields" :per-page=20 hover striped bordered small
													caption="<i class='icon-list'></i> List of Delivered Devices">
							<template v-slot:row-actions="row">
								<b-button v-if="row.row.item.status === 1" size="sm" variant="danger" class="mr-1" @click="showDeviceReplacementPopup(row)" active v-b-modal.device-replace-modal>
									<i class="icon-refresh"></i> Replace
								</b-button>
								<b-button size="sm" variant="info" class="mr-1" @click="showEditFormPopup(row)" active v-b-modal.device-edit-modal>
									<i class="icon-pencil"></i> Edit
								</b-button>
							</template>
						</genericTable>
					</div>
				</b-col>
			</b-row>
			<b-modal id="device-replace-modal"
							 :title="'Replace Device ' + replaceForm.oldIdentifier"
							 ok-title="Save"
							 @ok="handleOk"
							 @cancel="resetReplaceForm">
				<b-form @submit.prevent="checkReplaceFormValidity()" method="post">
					<b-form-group
						label="New Device Identifier"
						label-for="newIdentifier"
						:horizontal="true">
						<b-form-input v-model="replaceForm.newIdentifier" name="newIdentifier" id="newIdentifier" type="text" placeholder="Identifier" :state="$v.replaceForm.newIdentifier.$dirty ? !$v.replaceForm.newIdentifier.$error : null" aria-describedby="device-identifier-live-feedback"></b-form-input>
						<b-form-invalid-feedback id="device-identifier-live-feedback">This is a required field.</b-form-invalid-feedback>
					</b-form-group>
					<b-form-group
						label="New Device Model No."
						label-for="model-no"
						:horizontal="true">
						<b-form-input v-model="replaceForm.modelNo" name="modelNo" id="model-no" type="text" placeholder="Model No." aria-describedby="device-model-live-feedback"></b-form-input>
						<b-form-invalid-feedback id="device-model-live-feedback">This is a required field.</b-form-invalid-feedback>
					</b-form-group>
					<b-form-group
						label="Description"
						label-for="description"
						:horizontal="true">
						<b-form-textarea v-model="replaceForm.description" rows="4" max-rows="6" id="description"></b-form-textarea>
					</b-form-group>
					<b-form-group
						label="Remarks"
						label-for="remarks"
						:horizontal="true">
						<b-form-textarea v-model="replaceForm.remarks" rows="4" max-rows="6" id="remarks"></b-form-textarea>
					</b-form-group>
				</b-form>
			</b-modal>
			<b-modal id="device-edit-modal"
							 :title="'Edit Device ' + editForm.deviceIdentifier"
							 ok-title="Save"
							 @ok="handleOk"
							 @cancel="resetEditForm">
				<b-form @submit.prevent="checkEditFormValidity()" method="post">
					<b-form-group
						label="Model Number"
						label-for="edit-model-no"
						:horizontal="true">
						<b-form-input v-model="editForm.modelNo" type="text" id="edit-model-no"></b-form-input>
					</b-form-group>
					<b-form-group
						label="Description"
						label-for="edit-description"
						:horizontal="true">
						<b-form-textarea v-model="editForm.description" rows="4" max-rows="6" id="edit-description"></b-form-textarea>
					</b-form-group>
					<b-form-group
						label="Remarks"
						label-for="edit-remarks"
						:horizontal="true">
						<b-form-textarea v-model="editForm.remarks" rows="4" max-rows="6" id="edit-remarks"></b-form-textarea>
					</b-form-group>
				</b-form>
			</b-modal>
		</div>
	</div>
</template>

<script>
import genericTable from '@/components/GenericTable';
import toastService from "@/services/toastService";
import purchaseOrderService from "@/services/purchaseOrderService";
import deliveryDetailsService from "@/services/deliveryDetailsService";
import moment from "moment";
import { required, numeric, minValue, requiredIf } from "vuelidate/lib/validators";

export default {
	name : "ViewDeliveryDetails",
	components : {
		"genericTable" : genericTable
	},
	data : function () {
		return {
			purchaseOrderId : this.$route.params.purchaseOrderId,
			deliveredDevices : [],
			purchaseOrder    : null,
			totalAmount : 0,
			fields           : [
				{key: 'identifier', label: 'Device Identifier', sortable: true, class: 'text-center', thStyle: 'width: 10%'},
				{key: 'model_no', label: 'Model No.', class: 'text-center', thStyle: 'width: 10%'},
				{key: 'description', label: 'Description', class: 'text-center', thStyle: 'width: 29%'},
				{key: 'remarks', label: 'Remarks', class: 'text-center', thStyle: 'width: 29%'},
				{key: 'status', label : 'Status', class : 'text-center', thStyle: 'width: 7%'},
				{key: 'actions', label: 'Actions', class : 'text-center', thStyle: 'width: 15%'}
			],
			replaceForm      : {
				deliveryDetailsId : null,
				oldIdentifier : null,
				newIdentifier : null,
				modelNo: null,
				description: null,
				remarks : null
			},
			editForm : {
				deliveryDetailsId : null,
				deviceIdentifier: null,
				modelNo: null,
				description: null,
				remarks : null
			},
			loading : false
		}
	},
	validations : {
		replaceForm : {
			newIdentifier : {
				required
			}
		},
		editForm : {

		}
	},
	methods : {
		getPurchaseOrderDetails : function () {
			this.loading = true;
			purchaseOrderService.getPurchaseOrder(this.purchaseOrderId, (data) => {
				this.purchaseOrder = data;
				this.purchaseOrder.purchaseDate = moment(data.purchaseDate).format('DD-MM-YYYY');
				this.purchaseOrder.deliveryDate = moment(data.deliveryDate).format("DD-MM-YYYY");
				this.purchaseOrder.warrantyLastDate = moment(data.warrantyLastDate).format("DD-MM-YYYY");
				this.totalAmount = (this.purchaseOrder.unitPrice + this.purchaseOrder.softwarePackageFee) * this.purchaseOrder.deviceQuantity;
				this.getDeliveryDetails();
			}, (error) => {
				this.loading = false
				toastService.showErrorToast("Could not fetch purchase order details " + error);
			});
		},
		getDeliveryDetails : function () {
			deliveryDetailsService.getDeliveryDetails(this.purchaseOrderId, (data) => {
				this.createDeviceListTable(data);
				this.loading = false;
			}, (error) => {
				this.loading = false
				toastService.showErrorToast("Could not fetch purchase order details " + error);
			});
		},
		createDeviceListTable : function (data) {
			data.forEach((value, index) => {
				let row = new Object();
				row.id = value.id;
				row.identifier = value.deviceIdentifier;
				row.model_no = value.modelNo;
				row.description = value.description;
				row.remarks = value.remarks;
				row.status = value.status;
				this.deliveredDevices.push(row);
			});
		},
		showDeviceReplacementPopup : function (data) {
			this.replaceForm.deliveryDetailsId = data.row.item.id;
			this.replaceForm.oldIdentifier = data.row.item.identifier;
		},
		replaceDevice    : function () {
			const formData = {
				deliveryDetailsId : this.replaceForm.deliveryDetailsId,
				newIdentifier : this.replaceForm.newIdentifier,
				modelNo : this.replaceForm.modelNo,
				description : this.replaceForm.description,
				remarks : this.replaceForm.remarks
			};

			deliveryDetailsService.replaceDevice(formData, (data) => {
				this.resetReplaceForm();
				this.deliveredDevices = [];
				this.createDeviceListTable(data);
				toastService.showSuccessToast("Device replacement successful");
			}, (error) => {
				this.$nextTick(() => { this.$v.$reset() });
				toastService.showErrorToast("Could not replace device " + error);
			});
		},
		resetReplaceForm : function () {
			this.$nextTick(() => {
				this.$v.$reset();
				this.$bvModal.hide("device-replace-modal");
			});
			this.replaceForm.deliveryDetailsId = null;
			this.replaceForm.oldIdentifier = null;
			this.replaceForm.newIdentifier = null;
			this.replaceForm.modelNo = null;
			this.replaceForm.description = null;
			this.replaceForm.remarks = null;
		},
		resetEditForm : function () {
			this.$nextTick(() => {
				this.$v.$reset();
				this.$bvModal.hide("device-edit-modal");
			});
			this.editForm.deliveryDetailsId = null;
			this.editForm.deviceIdentifier = null;
			this.editForm.modelNo = null;
			this.editForm.description = null;
			this.editForm.remarks = null;
		},
		handleOk(event) {
			event.preventDefault();
			switch (event.componentId) {
				case "device-replace-modal" :
					this.checkReplaceFormValidity();
					break;
				case "device-edit-modal" :
					this.checkEditFormValidity();
					break;
			}
		},
		checkReplaceFormValidity : function () {
			let formHasErrors = false;

			this.$v.replaceForm.$touch();
			if (this.$v.replaceForm.$anyError) {
				formHasErrors = true;
			}

			if(formHasErrors) return;
			else this.replaceDevice();
		},
		checkEditFormValidity   : function () {
			let formHasErrors = false;

			this.$v.editForm.$touch();
			if (this.$v.editForm.$anyError) {
				formHasErrors = true;
			}

			if(formHasErrors) return;
			else this.updateDeliveryDetails();
		},
		downloadDeliveryReceipt : function (){
			deliveryDetailsService.downloadDeliveryReceipt(this.purchaseOrderId, (data) => {

			}, (error) => {
				toastService.showErrorToast("Could not download delivery receipt");
			});
		},
		showEditFormPopup       : function (data) {
			this.editForm.deliveryDetailsId = data.row.item.id;
			this.editForm.deviceIdentifier = data.row.item.identifier;
			this.editForm.modelNo = data.row.item.model_no;
			this.editForm.description = data.row.item.description;
			this.editForm.remarks = data.row.item.remarks;
		},
		updateDeliveryDetails   : function () {
			if((this.editForm.modelNo === null || this.editForm.modelNo === "")
				&& (this.editForm.description === null || this.editForm.description === "")
				&& (this.editForm.remarks === null || this.editForm.remarks === "")) {
				this.resetEditForm();
				return;
			}
			const formData = {
				modelNo : this.editForm.modelNo,
				description : this.editForm.description,
				remarks : this.editForm.remarks
			}

			deliveryDetailsService.updateDeliveryDetails(this.editForm.deliveryDetailsId, formData, (data) => {
				this.deliveredDevices.find(item => item.id === this.editForm.deliveryDetailsId).model_no = this.editForm.modelNo;
				this.deliveredDevices.find(item => item.id === this.editForm.deliveryDetailsId).description = this.editForm.description;
				this.deliveredDevices.find(item => item.id === this.editForm.deliveryDetailsId).remarks = this.editForm.remarks;
				this.resetEditForm();
				toastService.showSuccessToast("Remarks update successful");
			}, (error) => {
				this.$nextTick(() => { this.$v.$reset() });
				toastService.showErrorToast("Could not update remarks " + error);
			});
		}
	},
	created() {
		this.getPurchaseOrderDetails();
	},
	watch: {
		$route(to, from) {

		}
	},
	computed: {
		tableData : function(){
			return this.deliveredDevices;
		}
	}
}
</script>

<style scoped>

</style>
